// Vollea — Profile page with stats and edit gateway
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IoSettingsOutline, IoCreateOutline, IoCamera, IoLogOutOutline, IoShareSocialOutline } from "react-icons/io5";
import { Avatar, Card, SectionLabel } from "../components/ui-vollea";
import ShareCardModal from "../components/ShareCardModal";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { C, SP, R } from "../lib/theme";

export default function Profile() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [p, setP] = useState(null);
  const [showShare, setShowShare] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const data = await api.get(`/players/${user.user_id}`);
      setP(data);
    } catch {
      try { const me = await api.get("/profile/me"); setP(me); } catch {}
    }
  };

  useEffect(() => { if (user) load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [user]);

  const uploadPhoto = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { photo } = await api.post("/profile/photo", fd);
      setP((prev) => ({ ...prev, photo }));
    } catch {}
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  if (!p) return <div style={{ padding: SP.xl, textAlign: "center", color: C.lime }}>Loading…</div>;

  return (
    <div style={{ background: C.bg, minHeight: "100vh", paddingTop: SP.lg, paddingBottom: 90 }}>
      <div style={{ display: "flex", justifyContent: "flex-end", padding: `0 ${SP.lg}px` }}>
        <button onClick={() => navigate("/settings")} data-testid="profile-settings-btn" style={{ width: 40, height: 40, display: "flex", alignItems: "center", justifyContent: "center", color: C.text }}>
          <IoSettingsOutline size={22} />
        </button>
      </div>

      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: `0 ${SP.lg}px` }}>
        <div style={{ position: "relative" }}>
          <Avatar uri={p.photo} name={p.name} size={88} />
          <button onClick={() => fileRef.current?.click()} data-testid="profile-photo-button" style={{ position: "absolute", bottom: 0, right: 0, width: 32, height: 32, borderRadius: 16, background: C.lime, color: C.onLime, display: "flex", alignItems: "center", justifyContent: "center", border: `2px solid ${C.bg}` }}>
            <IoCamera size={14} />
          </button>
          <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => uploadPhoto(e.target.files?.[0])} />
        </div>
        <div data-testid="profile-name" style={{ color: C.text, fontWeight: 700, fontSize: 24, marginTop: SP.md }}>{p.name}</div>
        <div style={{ color: C.textMuted, fontSize: 14, marginTop: 4, textTransform: "capitalize" }}>
          {p.location || "No location"} · {p.skill_level || "beginner"}
        </div>

        <div style={{ display: "flex", gap: SP.sm, marginTop: SP.md, flexWrap: "wrap", justifyContent: "center" }}>
          <Tag>Side: {p.playing_side || "both"}</Tag>
          <Tag>Hand: {p.preferred_hand || "right"}</Tag>
          {!p.is_public && <Tag>🔒 Private</Tag>}
        </div>

        <button onClick={() => navigate("/profile/edit")} data-testid="profile-edit-btn" style={{ display: "inline-flex", alignItems: "center", gap: 6, background: C.lime, color: C.onLime, padding: `0 ${SP.lg}px`, height: 40, borderRadius: R.pill, marginTop: SP.lg, fontWeight: 700, fontSize: 12, letterSpacing: 1, textTransform: "uppercase" }}>
          <IoCreateOutline size={16} /> Edit profile
        </button>

        <button
          onClick={() => setShowShare(true)}
          data-testid="profile-share-btn"
          style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "transparent", border: `1px solid ${C.lime}`, color: C.lime, padding: `0 ${SP.lg}px`, height: 40, borderRadius: R.pill, marginTop: SP.sm, fontWeight: 700, fontSize: 12, letterSpacing: 1, textTransform: "uppercase", cursor: "pointer" }}
        >
          <IoShareSocialOutline size={16} /> Share player card
        </button>
      </div>

      <div style={{ padding: `${SP.xl}px ${SP.lg}px 0` }}>
        <Card style={{ background: C.surface2 }}>
          <div style={{ display: "flex" }}>
            <StatMini value={`#${p.current_ranking || "—"}`} label="Rank" accent />
            <StatMini value={p.points ?? 0} label="Points" />
            <StatMini value={`${p.win_percentage ?? 0}%`} label="Win Rate" />
          </div>
          <div style={{ height: 1, background: C.border, margin: `${SP.lg}px 0` }} />
          <div style={{ display: "flex" }}>
            <StatMini value={p.matches_played ?? 0} label="Played" />
            <StatMini value={p.wins ?? 0} label="Wins" />
            <StatMini value={p.losses ?? 0} label="Losses" />
            <StatMini value={`#${p.highest_ranking || "—"}`} label="Best" />
          </div>
        </Card>
      </div>

      {p.bio ? (
        <div style={{ padding: `${SP.xl}px ${SP.lg}px 0` }}>
          <SectionLabel>Bio</SectionLabel>
          <div style={{ color: C.text, fontSize: 14, lineHeight: 1.5 }}>{p.bio}</div>
        </div>
      ) : null}

      <div style={{ padding: `${SP.xl}px ${SP.lg}px 0` }}>
        <button onClick={handleLogout} data-testid="profile-logout-btn" style={{ width: "100%", height: 52, borderRadius: R.md, border: `1px solid ${C.borderStrong}`, color: C.error, fontWeight: 700, fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, letterSpacing: 1, textTransform: "uppercase" }}>
          <IoLogOutOutline size={18} /> Log out
        </button>
      </div>

      <ShareCardModal
        open={showShare}
        onClose={() => setShowShare(false)}
        variant="player"
        player={p}
        fileBaseName={`vollea-player-${(p.name || "card").toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
      />
    </div>
  );
}

function Tag({ children }) {
  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 4, background: C.surface, padding: `6px ${SP.md}px`, borderRadius: R.pill, border: `1px solid ${C.border}`, color: C.textMuted, fontSize: 12, textTransform: "capitalize" }}>
      {children}
    </div>
  );
}

function StatMini({ value, label, accent }) {
  return (
    <div style={{ flex: 1, textAlign: "center" }}>
      <div style={{ color: accent ? C.lime : C.text, fontWeight: 700, fontSize: 20 }}>{value}</div>
      <div style={{ color: C.textMuted, fontSize: 11, fontWeight: 600, marginTop: 4, letterSpacing: 0.5 }}>{label}</div>
    </div>
  );
}
